import { Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

export const ProductCombobox = ({
  onSelect,
  inputRef,
  products,
  placeholder = "Ajouter un produit",
  testIdPrefix = "movement-product",
  value,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const selectedProduct = products.find((product) => product.product_id === value);
  const normalizedQuery = query.trim().toLocaleLowerCase("fr-FR");

  const allMatches = useMemo(() => {
    if (!normalizedQuery) {
      return [];
    }

    return products.filter((product) => {
      const matchesName = product.name.toLocaleLowerCase("fr-FR").includes(normalizedQuery);
      const matchesTag = (product.tags || []).some((tag) => (
        tag.toLocaleLowerCase("fr-FR").includes(normalizedQuery)
      ));

      return matchesName || matchesTag;
    });
  }, [products, normalizedQuery]);
  const matches = allMatches.slice(0, 12);
  const hasMoreMatches = allMatches.length > matches.length;

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);

    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  const selectProduct = (product) => {
    onSelect(product);
    setQuery("");
    setIsOpen(false);
  };

  const clearSelection = () => {
    onSelect(null);
    setQuery("");
    setIsOpen(true);
  };

  return (
    <div
      className="product-combobox"
      data-testid={`${testIdPrefix}-combobox`}
      ref={containerRef}
    >
      <Search aria-hidden="true" size={16} />
      <input
        aria-controls={`${testIdPrefix}-options`}
        aria-expanded={isOpen}
        aria-label={placeholder}
        autoComplete="off"
        data-arrow-navigation-ignore={normalizedQuery ? "true" : undefined}
        data-testid={`${testIdPrefix}-combobox-input`}
        onChange={(event) => {
          setQuery(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setIsOpen(false);
          }

          if (event.key === "Enter" && isOpen && normalizedQuery) {
            event.preventDefault();
            if (matches.length) {
              selectProduct(matches[0]);
            }
          }
        }}
        placeholder={placeholder}
        ref={inputRef}
        role="combobox"
        value={isOpen ? query : selectedProduct?.name || ""}
      />
      {selectedProduct && (
        <button
          aria-label="Retirer le produit sélectionné"
          data-testid={`${testIdPrefix}-combobox-clear-button`}
          onClick={clearSelection}
          title="Retirer le produit sélectionné"
          type="button"
        >
          <X size={15} />
        </button>
      )}
      {isOpen && (
        <div
          className="product-combobox-menu"
          data-testid={`${testIdPrefix}-combobox-menu`}
          id={`${testIdPrefix}-options`}
          role="listbox"
        >
          {!normalizedQuery && (
            <p data-testid={`${testIdPrefix}-combobox-prompt`}>
              Saisissez un nom ou un tag pour rechercher un produit.
            </p>
          )}
          {normalizedQuery && matches.length ? (
            matches.map((product) => (
              <button
                aria-selected={product.product_id === value}
                className="product-combobox-option"
                data-testid={`${testIdPrefix}-option-${product.product_id}`}
                key={product.product_id}
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectProduct(product);
                }}
                role="option"
                type="button"
              >
                <strong>{product.name}</strong>
              </button>
            ))
          ) : normalizedQuery ? (
            <p data-testid={`${testIdPrefix}-combobox-empty`}>Aucun produit correspondant.</p>
          ) : null}
          {hasMoreMatches && (
            <p data-testid={`${testIdPrefix}-combobox-refine`}>
              Affinez votre recherche pour voir les autres produits.
            </p>
          )}
        </div>
      )}
    </div>
  );
};